import { decodeMutationBatch, encodeMutationBatch } from "@dopejs/pingo-reconciler";
import { MutationTransportBackpressureError } from "./transport-errors";
const TRANSPORT_VERSION = 1;
const MAX_BUFFERED_BYTES = 256 * 1024 * 1024;
/** Main-side bounded queue around the shared ring; normal pressure never fatally fails a root. */
export class SabMutationTransport {
    #endpoint;
    #acknowledgementTimeoutMs;
    #maxBufferedBytes;
    #maxBufferedFrames;
    #onError;
    #ring;
    #sessionId;
    #pending = [];
    #published = [];
    #drainWaiters = [];
    #acknowledged = 0;
    #ackTimer;
    #bufferedBytes = 0;
    #closed = false;
    #failure;
    #highWatermark = 0;
    #latestAcknowledgedSequence = 0;
    #latestAcceptedSequence = 0;
    #merged = 0;
    #rejected = 0;
    #timeouts = 0;
    constructor(endpoint, ring, options) {
        this.#endpoint = endpoint;
        this.#acknowledgementTimeoutMs = boundedInteger(options.acknowledgementTimeoutMs ?? 5_000, 1, 60_000, "acknowledgementTimeoutMs");
        this.#ring = ring;
        this.#sessionId = positiveU32(options.sessionId, "sessionId");
        this.#maxBufferedFrames = boundedInteger(options.maxBufferedFrames ?? 128, 2, 4096, "maxBufferedFrames");
        this.#maxBufferedBytes = boundedInteger(options.maxBufferedBytes ?? 64 * 1024 * 1024, 4, MAX_BUFFERED_BYTES, "maxBufferedBytes");
        this.#onError = options.onError;
        endpoint.addEventListener("message", this.#handleMessage);
    }
    enqueue(frameSeq, bytes) {
        this.assertOpen();
        const sequence = positiveU32(frameSeq, "frameSeq");
        if (!(bytes instanceof Uint8Array))
            throw new TypeError("mutation payload must be Uint8Array");
        if (bytes.byteLength === 0 || bytes.byteLength % 4 !== 0) {
            throw new RangeError("mutation payload must be non-empty and four-byte aligned");
        }
        if (bytes.byteLength > this.#ring.payloadBytes) {
            throw new RangeError("mutation payload exceeds the SAB slot budget");
        }
        if (this.#latestAcceptedSequence !== 0 &&
            sequence !== nextFrameSequence(this.#latestAcceptedSequence)) {
            throw new Error("mutation frame sequence is not contiguous");
        }
        const bufferedFrames = this.#pending.length + this.#published.length;
        if (bufferedFrames >= this.#maxBufferedFrames ||
            this.#bufferedBytes + bytes.byteLength > this.#maxBufferedBytes) {
            if (this.mergeNewestPending(sequence, bytes))
                return;
            this.#rejected += 1;
            throw new MutationTransportBackpressureError("SAB mutation buffer is full");
        }
        this.#pending.push({ byteLength: bytes.byteLength, bytes: bytes.slice(), frameSeq: sequence });
        this.#bufferedBytes += bytes.byteLength;
        this.#latestAcceptedSequence = sequence;
        this.#highWatermark = Math.max(this.#highWatermark, bufferedFrames + 1);
        this.flush();
    }
    drain() {
        if (this.#failure !== undefined)
            return Promise.reject(this.#failure);
        if (this.#pending.length === 0 && this.#published.length === 0)
            return Promise.resolve();
        return new Promise((resolve, reject) => this.#drainWaiters.push({ reject, resolve }));
    }
    close() {
        this.#closed = true;
        return this.drain().finally(() => {
            this.#ring.close();
            this.detach();
            this.wake();
        });
    }
    abort(reason = new Error("SAB mutation transport aborted")) {
        if (this.#failure !== undefined)
            return;
        this.#ring.close();
        this.fail(reason);
    }
    metrics() {
        return {
            acknowledged: this.#acknowledged,
            bufferedBytes: this.#bufferedBytes,
            highWatermark: this.#highWatermark,
            latestAcknowledgedSequence: this.#latestAcknowledgedSequence,
            latestAcceptedSequence: this.#latestAcceptedSequence,
            merged: this.#merged,
            mode: "sab",
            publishedAwaitingAck: this.#published.length,
            queued: this.#pending.length,
            rejected: this.#rejected,
            ring: this.#ring.metrics(),
            timeouts: this.#timeouts,
        };
    }
    #handleMessage = (event) => {
        const message = event.data;
        if (!isResponseEnvelope(message))
            return;
        if (!isPositiveU32(message.sessionId)) {
            this.fail(new Error("SAB response has an invalid session"));
            return;
        }
        if (message.sessionId !== this.#sessionId)
            return;
        if (!isResponse(message)) {
            this.fail(new Error("SAB response is malformed"));
            return;
        }
        if (message.version !== TRANSPORT_VERSION) {
            this.fail(new Error("SAB transport version mismatch"));
            return;
        }
        if (message.kind === "pingo:sab-error") {
            this.fail(new Error(`render Worker rejected SAB mutation: ${message.error}`));
            return;
        }
        const expected = this.#published[0];
        if (expected === undefined || message.frameSeq !== expected.frameSeq) {
            this.fail(new Error("SAB acknowledgement is missing, duplicate, or out of order"));
            return;
        }
        this.#published.shift();
        this.clearAcknowledgementTimer();
        this.#bufferedBytes -= expected.byteLength;
        this.#acknowledged += 1;
        this.#latestAcknowledgedSequence = message.frameSeq;
        this.flush();
        this.armAcknowledgementTimer();
        this.resolveDrainIfEmpty();
    };
    flush() {
        let publishedAny = false;
        while (this.#failure === undefined && this.#pending.length > 0) {
            const frame = this.#pending[0];
            if (frame === undefined || !this.#ring.tryPublish(frame.frameSeq, frame.bytes))
                break;
            this.#pending.shift();
            this.#published.push({ byteLength: frame.byteLength, frameSeq: frame.frameSeq });
            publishedAny = true;
        }
        if (publishedAny)
            this.wake();
        this.armAcknowledgementTimer();
    }
    wake() {
        const message = {
            kind: "pingo:sab-wake",
            sessionId: this.#sessionId,
            version: TRANSPORT_VERSION,
        };
        try {
            this.#endpoint.postMessage(message);
        }
        catch (cause) {
            this.fail(toError(cause, "SAB Worker wake failed"));
        }
    }
    assertOpen() {
        if (this.#failure !== undefined)
            throw this.#failure;
        if (this.#closed)
            throw new Error("SAB mutation transport is closed");
    }
    mergeNewestPending(frameSeq, bytes) {
        const previous = this.#pending.at(-1);
        if (previous === undefined)
            return false;
        let merged;
        try {
            const first = decodeMutationBatch(previous.bytes);
            const second = decodeMutationBatch(bytes);
            if (first.frameSeq !== previous.frameSeq || second.frameSeq !== frameSeq)
                return false;
            merged = encodeMutationBatch({
                frameSeq,
                mutations: [...first.mutations, ...second.mutations],
            });
        }
        catch {
            return false;
        }
        if (merged.byteLength > this.#ring.payloadBytes)
            return false;
        const nextBufferedBytes = this.#bufferedBytes - previous.byteLength + merged.byteLength;
        if (nextBufferedBytes > this.#maxBufferedBytes)
            return false;
        this.#pending[this.#pending.length - 1] = {
            byteLength: merged.byteLength,
            bytes: merged,
            frameSeq,
        };
        this.#bufferedBytes = nextBufferedBytes;
        this.#latestAcceptedSequence = frameSeq;
        this.#merged += 1;
        return true;
    }
    fail(error) {
        if (this.#failure !== undefined)
            return;
        this.#failure = error;
        this.#closed = true;
        this.clearAcknowledgementTimer();
        this.detach();
        for (const waiter of this.#drainWaiters.splice(0))
            waiter.reject(error);
        this.#onError?.(error);
    }
    resolveDrainIfEmpty() {
        if (this.#pending.length !== 0 || this.#published.length !== 0)
            return;
        for (const waiter of this.#drainWaiters.splice(0))
            waiter.resolve();
    }
    detach() {
        this.clearAcknowledgementTimer();
        this.#endpoint.removeEventListener("message", this.#handleMessage);
    }
    armAcknowledgementTimer() {
        if (this.#ackTimer !== undefined || this.#failure !== undefined || this.#published.length === 0)
            return;
        this.#ackTimer = globalThis.setTimeout(() => {
            this.#ackTimer = undefined;
            this.#timeouts += 1;
            this.fail(new Error("SAB mutation acknowledgement timed out"));
        }, this.#acknowledgementTimeoutMs);
    }
    clearAcknowledgementTimer() {
        if (this.#ackTimer === undefined)
            return;
        clearTimeout(this.#ackTimer);
        this.#ackTimer = undefined;
    }
}
/** Worker-side shared-ring consumer; drain may be called by wake messages and every render tick. */
export class SabMutationReceiver {
    #consume;
    #endpoint;
    #onError;
    #ring;
    #sessionId;
    #disposed = false;
    #lastSequence = 0;
    #processing = Promise.resolve();
    constructor(endpoint, ring, consume, options) {
        this.#endpoint = endpoint;
        this.#ring = ring;
        this.#consume = consume;
        this.#sessionId = positiveU32(options.sessionId, "sessionId");
        this.#onError = options.onError;
        endpoint.addEventListener("message", this.#handleMessage);
    }
    /** Moves every currently committed slot into the ordered async processing chain. */
    drain() {
        if (this.#disposed)
            return 0;
        let queued = 0;
        while (true) {
            const frame = this.#ring.take();
            if (frame === null)
                break;
            queued += 1;
            this.#processing = this.#processing
                .then(async () => {
                if (this.#disposed)
                    return;
                if (this.#lastSequence !== 0 && !isNewerSequence(frame.frameSeq, this.#lastSequence)) {
                    throw new Error("SAB mutation sequence is not newer");
                }
                await this.#consume(frame.frameSeq, frame.bytes);
                this.#lastSequence = frame.frameSeq;
                const acknowledgement = {
                    frameSeq: frame.frameSeq,
                    kind: "pingo:sab-ack",
                    sessionId: this.#sessionId,
                    version: TRANSPORT_VERSION,
                };
                this.#endpoint.postMessage(acknowledgement);
            })
                .catch((cause) => this.handleFailure(cause));
        }
        return queued;
    }
    whenIdle() {
        return this.#processing;
    }
    dispose() {
        if (this.#disposed)
            return;
        this.#disposed = true;
        this.#endpoint.removeEventListener("message", this.#handleMessage);
    }
    #handleMessage = (event) => {
        const message = event.data;
        if (!isWakeEnvelope(message) || this.#disposed)
            return;
        if (!isPositiveU32(message.sessionId)) {
            this.handleFailure(new Error("SAB wake has an invalid session"));
            return;
        }
        if (message.sessionId !== this.#sessionId)
            return;
        if (!isWake(message)) {
            this.handleFailure(new Error("SAB wake is malformed"));
            return;
        }
        if (message.version !== TRANSPORT_VERSION) {
            this.handleFailure(new Error("SAB transport version mismatch"));
            return;
        }
        this.drain();
    };
    handleFailure(cause) {
        if (this.#disposed)
            return;
        const error = toError(cause, "render Worker SAB mutation failed");
        const message = {
            error: error.message,
            kind: "pingo:sab-error",
            sessionId: this.#sessionId,
            version: TRANSPORT_VERSION,
        };
        try {
            this.#endpoint.postMessage(message);
        }
        catch {
            // The main-side Worker error/exit path owns recovery when messaging is gone.
        }
        this.#onError?.(error);
        this.dispose();
    }
}
function isWake(value) {
    if (!isWakeEnvelope(value))
        return false;
    const message = value;
    return (message.kind === "pingo:sab-wake" &&
        isPositiveU32(message.sessionId) &&
        Number.isInteger(message.version));
}
function isWakeEnvelope(value) {
    return (typeof value === "object" &&
        value !== null &&
        value.kind === "pingo:sab-wake");
}
function isResponse(value) {
    if (!isResponseEnvelope(value))
        return false;
    if (!isPositiveU32(value.sessionId) || !Number.isInteger(value.version))
        return false;
    return value.kind === "pingo:sab-ack"
        ? isPositiveU32(value.frameSeq)
        : typeof value.error === "string";
}
function isResponseEnvelope(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const kind = value.kind;
    return kind === "pingo:sab-ack" || kind === "pingo:sab-error";
}
function isPositiveU32(value) {
    return Number.isInteger(value) && value >= 1 && value <= 0xffff_ffff;
}
function nextFrameSequence(value) {
    const next = (value + 1) >>> 0;
    return next === 0 ? 1 : next;
}
function isNewerSequence(candidate, previous) {
    const distance = (candidate - previous) >>> 0;
    return distance !== 0 && distance < 0x8000_0000;
}
function positiveU32(value, label) {
    if (!Number.isInteger(value) || value < 1 || value > 0xffff_ffff) {
        throw new RangeError(`${label} must be a positive u32`);
    }
    return value;
}
function boundedInteger(value, minimum, maximum, label) {
    if (!Number.isInteger(value) || value < minimum || value > maximum) {
        throw new RangeError(`${label} must be an integer from ${String(minimum)} to ${String(maximum)}`);
    }
    return value;
}
function toError(cause, message) {
    return cause instanceof Error ? cause : new Error(message, { cause });
}
//# sourceMappingURL=sab-transport.js.map